import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { enumReadSchema } from "../../core/enum-read.schema";

/**
 * The `VibeUsageType` values that the consumption ledger records and
 * that the cost-safety system rolls up + caps.
 *
 * Single source of truth — consumed by:
 *
 *   - The `COST_SAFETY_AUTO_SUSPENDED` audit payload schema's
 *     `usageType` Zod literal — `VibeUsageTypeLiteralSchema`.
 *   - The auto-transition use case's iteration order
 *     (`VIBE_USAGE_TYPES` is the deterministic "first-breach-wins"
 *     order so the same underlying breach always emits the same
 *     audit payload).
 *   - The cap-resolution lookup
 *     (`VIBE_USAGE_TYPE_TO_CAP_KEY`) that maps each usage type to
 *     its `VibeOrgConsumptionCap.effectiveCaps()` column.
 *   - The CLI / log surface labels (`VIBE_USAGE_TYPE_LABELS`)
 *     shown to operators + users.
 *
 * The `as const satisfies readonly VibeUsageTypeLiteral[]`
 * constraint pins each literal as a valid Prisma enum value, and
 * the `Record<VibeUsageTypeLiteral, …>` constraints on the maps
 * force exhaustiveness — adding a new `VibeUsageType` to the
 * Prisma enum triggers compile errors on the maps until every
 * site is updated. Drift impossible.
 */
type VibeUsageTypeLiteralPrisma = (typeof DbEnum.VIBE_USAGE_TYPE_VALUES)[number];

export const VIBE_USAGE_TYPES = [
  "VIBE_COMPUTE_MIN",
  "VIBE_BUILD_MIN",
  "VIBE_EGRESS_MB",
  // Appended, never inserted. This tuple's ORDER is the deterministic
  // first-breach-wins order the auto-transition use case iterates, so
  // reordering it silently changes which usageType an org's audit payload
  // names when two types breach in the same rollup.
  "VIBE_BACKUP_MIN"
] as const satisfies readonly VibeUsageTypeLiteralPrisma[];

export type VibeUsageTypeLiteral = (typeof VIBE_USAGE_TYPES)[number];

export const VibeUsageTypeLiteralSchema = z.enum(VIBE_USAGE_TYPES);

/** `VibeUsageTypeLiteralSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeUsageTypeReadSchema = enumReadSchema(VIBE_USAGE_TYPES);

/**
 * Exhaustiveness check: every `VibeUsageType` must appear in the
 * `VIBE_USAGE_TYPES` tuple. Adding a new value to the Prisma enum
 * without adding it here triggers a compile error on `_check`.
 */
type _MissingFromOrder = Exclude<VibeUsageTypeLiteralPrisma, VibeUsageTypeLiteral>;
const _check: _MissingFromOrder extends never ? true : never = true;
void _check;

/**
 * Maps each `VibeUsageType` to the matching column on
 * `VibeOrgConsumptionCap.effectiveCaps()`. The auto-transition use
 * case dereferences this map per usageType to resolve the
 * effective cap for the comparison.
 */
export const VIBE_USAGE_TYPE_TO_CAP_KEY: Record<
  VibeUsageTypeLiteral,
  "computeMinCap" | "buildMinCap" | "egressMbCap" | "backupMinCap"
> = {
  VIBE_COMPUTE_MIN: "computeMinCap",
  VIBE_BUILD_MIN: "buildMinCap",
  VIBE_EGRESS_MB: "egressMbCap",
  VIBE_BACKUP_MIN: "backupMinCap"
} as const;

/**
 * Human-readable labels surfaced in audit-reason strings + CLI +
 * log lines. Kept on this side rather than in i18n because the
 * audit-event's `VibeOrgCostSafetyState.suspendedReason` is the
 * customer-visible "why was I gated" message and must read
 * naturally in English at v1.
 */
export const VIBE_USAGE_TYPE_LABELS: Record<VibeUsageTypeLiteral, string> = {
  VIBE_COMPUTE_MIN: "compute-minutes",
  VIBE_BUILD_MIN: "build-minutes",
  VIBE_EGRESS_MB: "egress-MB",
  VIBE_BACKUP_MIN: "backup-minutes"
} as const;
